import { marked } from "marked";
import { helpCentreSections } from "~/config/help-centre";

export type ContentItem = {
  slug: string;
  title: string;
  description: string;
  date?: string;
  updated?: string;
  readingTime?: string;
  category?: string;
  section?: string;
  order: number;
  body: string;
};
const markdownFiles = import.meta.glob("/content/**/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
}) as Record<string, string>;
const legacyKnowledgeBasePaths: Record<string, string> = {
  "backup-basics": "hosting-tools/backup-full",
  "website-care/backup-full": "hosting-tools/backup-full",
  "connect-a-domain": "domains/connect-a-domain",
  "shared-hosting-comparisons": "comparisons/shared-hosting",
  "application-hosting-comparisons": "comparisons/application-hosting",
  "database-hosting-comparisons": "comparisons/databases",
  "hostright-vs-hostinger-shared-hosting":
    "comparisons/shared-hosting/hostright-vs-hostinger",
  "hostright-vs-render": "comparisons/application-hosting/hostright-vs-render",
  "hostright-vs-heroku": "comparisons/application-hosting/hostright-vs-heroku",
};
function parse(raw: string, slug: string, section?: string): ContentItem {
  const [, front = "", body = ""] = raw.split(/^---$/m);
  const field = (key: string) =>
    front.match(new RegExp(`^${key}:\\s*["']?(.+?)["']?\\s*$`, "m"))?.[1] ?? "";
  return {
    slug,
    title: field("title"),
    description: field("description"),
    date: field("date"),
    updated: field("updated"),
    readingTime: field("readingTime"),
    category: field("category"),
    section,
    order: Number(field("order")) || 100,
    body,
  };
}
function contentPath(path: string, kind: "blog" | "knowledge-base" | "legal") {
  const relative = path.split(`/content/${kind}/`)[1]!.replace(/\.md$/, "");
  const parts = relative.split("/");
  if (parts.at(-1) === "index") parts.pop();
  return parts.join("/");
}
export function getContent(kind: "blog" | "knowledge-base" | "legal") {
  return Object.entries(markdownFiles)
    .filter(([path]) => path.includes(`/content/${kind}/`))
    .map(([path, raw]) => {
      const slug = contentPath(path, kind);
      return parse(
        raw,
        slug,
        kind === "knowledge-base" ? slug.split("/")[0] : undefined,
      );
    })
    .sort((a, b) =>
      kind === "knowledge-base"
        ? a.order - b.order || a.slug.localeCompare(b.slug)
        : (b.date ?? "").localeCompare(a.date ?? ""),
    );
}
export function getCanonicalKnowledgeBasePath(slug: string) {
  return legacyKnowledgeBasePaths[slug] ?? slug;
}
export function getContentBySlug(
  kind: "blog" | "knowledge-base" | "legal",
  slug: string,
) {
  return getContent(kind).find(
    (item) =>
      item.slug ===
      (kind === "knowledge-base" ? getCanonicalKnowledgeBasePath(slug) : slug),
  );
}
export function getContentSections() {
  const metadata = new Map<
    string,
    { id: string; label: string; order: number }
  >(helpCentreSections.map((section) => [section.id, section]));
  const grouped = getContent("knowledge-base").reduce<
    Record<string, { label: string; order: number; items: ContentItem[] }>
  >((sections, item) => {
    const id = item.section ?? "guides";
    const configured = metadata.get(id);
    (sections[id] ??= {
      label: configured?.label ?? "Guides",
      order: configured?.order ?? 999,
      items: [],
    }).items.push(item);
    return sections;
  }, {});
  return Object.fromEntries(
    Object.entries(grouped).sort(([, a], [, b]) => a.order - b.order),
  );
}
function sectionTitle(segment: string) {
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
function KnowledgeBaseSidebar({ currentSlug }: { currentSlug: string }) {
  const sections = getContentSections();
  const currentSection = Object.entries(sections).find(([, section]) =>
    section.items.some((entry) => entry.slug === currentSlug),
  );
  const comparisonSection = sections.comparisons;
  const comparisonOverview = comparisonSection?.items.find(
    (entry) => entry.slug === "comparisons",
  );
  const comparisonGroups = comparisonSection
    ? Object.entries(
        comparisonSection.items
          .filter((entry) => entry.slug !== "comparisons")
          .reduce<Record<string, ContentItem[]>>((groups, entry) => {
            const group = entry.slug.split("/")[1] ?? "general";
            (groups[group] ??= []).push(entry);
            return groups;
          }, {}),
      )
    : [];
  const applicationSection = sections["application-hosting"];
  const applicationGroups = applicationSection
    ? Object.entries(
        applicationSection.items.reduce<Record<string, ContentItem[]>>(
          (groups, entry) => {
            const group = entry.slug.split("/")[1] ?? "overview";
            (groups[group] ??= []).push(entry);
            return groups;
          },
          {},
        ),
      )
    : [];
  return (
    <aside className="lg:sticky lg:top-26 lg:self-start">
      <div className="hide-scrollbar border-t border-ink/10 pt-5 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:overscroll-contain lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0 lg:pr-2">
        <a
          href="/knowledge-base"
          className="inline-flex items-center gap-2 text-sm font-bold transition hover:text-moss"
        >
          <span>All Help Centre guides</span>
          <span aria-hidden="true">↗</span>
        </a>
        <div className="mt-7">
          <p className="eyebrow text-moss">Explore</p>
          <nav className="mt-3 grid gap-1" aria-label="Help Centre sections">
            {Object.entries(sections).map(([id, section]) => (
              <details
                key={id}
                open={currentSection?.[0] === id}
                className="group border-b border-ink/8 py-1.5 last:border-b-0"
              >
                <summary
                  className={`flex cursor-pointer list-none items-center justify-between gap-3 py-1 text-sm font-semibold transition hover:text-moss ${currentSection?.[0] === id ? "text-moss" : "text-ink/70"}`}
                >
                  <span>{section.label}</span>
                  <span
                    aria-hidden="true"
                    className="text-base leading-none transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                {id === "comparisons" ? (
                  <div className="grid gap-1 pb-2 pt-2">
                    {comparisonOverview && (
                      <a
                        href={`/knowledge-base/${comparisonOverview.slug}`}
                        aria-current={
                          comparisonOverview.slug === currentSlug
                            ? "page"
                            : undefined
                        }
                        className={`border-l-2 pl-3 text-sm leading-5 transition ${comparisonOverview.slug === currentSlug ? "border-leaf font-bold text-ink" : "border-transparent text-ink/55 hover:border-cloud hover:text-ink"}`}
                      >
                        {comparisonOverview.title}
                      </a>
                    )}
                    {comparisonGroups.map(([group, entries]) => {
                      const overview = entries.find(
                        (entry) => entry.slug === `comparisons/${group}`,
                      );
                      const children = entries.filter(
                        (entry) => entry !== overview,
                      );
                      const active = entries.some(
                        (entry) => entry.slug === currentSlug,
                      );
                      if (children.length === 0 && overview) {
                        return (
                          <a
                            key={overview.slug}
                            href={`/knowledge-base/${overview.slug}`}
                            aria-current={
                              overview.slug === currentSlug ? "page" : undefined
                            }
                            className={`border-l-2 pl-3 text-sm leading-5 transition ${overview.slug === currentSlug ? "border-leaf font-bold text-ink" : "border-transparent text-ink/55 hover:border-cloud hover:text-ink"}`}
                          >
                            {overview.title}
                          </a>
                        );
                      }
                      return (
                        <details
                          key={group}
                          open={active}
                          className="group/nested rounded-xl"
                        >
                          <summary
                            className={`flex cursor-pointer list-none items-center justify-between gap-2 px-2 py-1.5 text-sm transition hover:text-moss ${active ? "font-bold text-moss" : "text-ink/65"}`}
                          >
                            <span>
                              {overview?.title ?? sectionTitle(group)}
                            </span>
                            <span className="flex items-center gap-2">
                              <span
                                aria-hidden="true"
                                className="text-sm transition-transform group-open/nested:rotate-45"
                              >
                                +
                              </span>
                              {overview && (
                                <a
                                  href={`/knowledge-base/${overview.slug}`}
                                  aria-label={`Read ${overview.title}`}
                                  className="text-xs text-ink/45 transition hover:text-moss"
                                >
                                  ↗
                                </a>
                              )}
                            </span>
                          </summary>
                          <div className="grid gap-2 pb-2 pl-3 pt-1">
                            {children.map((entry) => (
                              <a
                                key={entry.slug}
                                href={`/knowledge-base/${entry.slug}`}
                                aria-current={
                                  entry.slug === currentSlug
                                    ? "page"
                                    : undefined
                                }
                                className={`border-l-2 pl-3 text-sm leading-5 transition ${entry.slug === currentSlug ? "border-leaf font-bold text-ink" : "border-transparent text-ink/55 hover:border-cloud hover:text-ink"}`}
                              >
                                {entry.title}
                              </a>
                            ))}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                ) : id === "application-hosting" ? (
                  <div className="grid gap-1 pb-2 pt-2">
                    {applicationGroups.map(([group, entries]) => {
                      const overview = entries.find(
                        (entry) => entry.slug === "application-hosting",
                      );
                      if (group === "overview" && overview) {
                        return (
                          <a
                            key={overview.slug}
                            href={`/knowledge-base/${overview.slug}`}
                            aria-current={
                              overview.slug === currentSlug ? "page" : undefined
                            }
                            className={`border-l-2 pl-3 text-sm leading-5 transition ${overview.slug === currentSlug ? "border-leaf font-bold text-ink" : "border-transparent text-ink/55 hover:border-cloud hover:text-ink"}`}
                          >
                            {overview.title}
                          </a>
                        );
                      }
                      const parent = entries.find(
                        (entry) =>
                          entry.slug === `application-hosting/${group}`,
                      );
                      const children = entries.filter(
                        (entry) => entry !== parent,
                      );
                      const active = entries.some(
                        (entry) => entry.slug === currentSlug,
                      );
                      return (
                        <details
                          key={group}
                          open={active}
                          className="group/nested rounded-xl"
                        >
                          <summary
                            className={`flex cursor-pointer list-none items-center justify-between gap-2 px-2 py-1.5 text-sm transition hover:text-moss ${active ? "font-bold text-moss" : "text-ink/65"}`}
                          >
                            <span>{parent?.title ?? sectionTitle(group)}</span>
                            <span className="flex items-center gap-2">
                              <span
                                aria-hidden="true"
                                className="text-sm transition-transform group-open/nested:rotate-45"
                              >
                                +
                              </span>
                              {parent && (
                                <a
                                  href={`/knowledge-base/${parent.slug}`}
                                  aria-label={`Read ${parent.title}`}
                                  className="text-xs text-ink/45 transition hover:text-moss"
                                >
                                  ↗
                                </a>
                              )}
                            </span>
                          </summary>
                          <div className="grid gap-2 pb-2 pl-3 pt-1">
                            {children.map((entry) => (
                              <a
                                key={entry.slug}
                                href={`/knowledge-base/${entry.slug}`}
                                aria-current={
                                  entry.slug === currentSlug
                                    ? "page"
                                    : undefined
                                }
                                className={`border-l-2 pl-3 text-sm leading-5 transition ${entry.slug === currentSlug ? "border-leaf font-bold text-ink" : "border-transparent text-ink/55 hover:border-cloud hover:text-ink"}`}
                              >
                                {entry.title}
                              </a>
                            ))}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid gap-2 pb-2 pt-2">
                    {section.items.map((entry) => (
                      <a
                        key={entry.slug}
                        href={`/knowledge-base/${entry.slug}`}
                        aria-current={
                          entry.slug === currentSlug ? "page" : undefined
                        }
                        className={`border-l-2 pl-3 text-sm leading-5 transition ${entry.slug === currentSlug ? "border-leaf font-bold text-ink" : "border-transparent text-ink/55 hover:border-cloud hover:text-ink"}`}
                      >
                        {entry.title}
                      </a>
                    ))}
                  </div>
                )}
              </details>
            ))}
          </nav>
        </div>
      </div>
    </aside>
  );
}

export function MarkdownArticle({
  item,
  sidebar = false,
}: {
  item: ContentItem;
  sidebar?: boolean;
}) {
  return (
    <div
      className={`mx-auto grid max-w-7xl gap-10 px-5 py-14 lg:px-12 lg:py-18 ${sidebar ? "lg:grid-cols-[minmax(0,1fr)_12rem]" : ""}`}
    >
      <article
        className={`prose ${sidebar ? "max-w-4xl" : "mx-auto max-w-4xl"}`}
      >
        <p className="eyebrow text-moss">
          {item.category ?? "HostRight guide"}
        </p>
        <h1 className="display mt-4 max-w-4xl text-5xl font-bold leading-[.95] sm:text-6xl">
          {item.title}
        </h1>
        <p className="mt-6 max-w-3xl text-xl leading-8 text-ink/65">
          {item.description}
        </p>
        {item.date && (
          <p className="mt-5 text-sm text-ink/50">
            Published {item.date}
            {item.updated && item.updated !== item.date
              ? ` · Updated ${item.updated}`
              : ""}
            {item.readingTime ? ` · ${item.readingTime}` : ""}
          </p>
        )}
        <div
          className="markdown-body mt-12 max-w-3xl border-t border-ink/10 pt-3"
          dangerouslySetInnerHTML={{
            __html: marked.parse(item.body, { gfm: true, breaks: false }),
          }}
        />
      </article>
      {sidebar && <KnowledgeBaseSidebar currentSlug={item.slug} />}
    </div>
  );
}
export function ContentCard({
  item,
  kind,
}: {
  item: ContentItem;
  kind: "blog" | "knowledge-base";
}) {
  const href =
    kind === "blog" ? `/blog/${item.slug}` : `/knowledge-base/${item.slug}`;
  return (
    <a
      href={href}
      className="group block rounded-3xl border border-ink/10 bg-white p-6 transition duration-300 hover:-translate-y-1 hover:border-moss hover:shadow-[0_20px_40px_rgba(16,37,31,.1)]"
    >
      <h2 className="text-2xl font-bold tracking-tight">{item.title}</h2>
      <p className="mt-3 leading-7 text-ink/60">{item.description}</p>
      <div className="mt-7 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm font-bold">
        <span className="text-ink/50">{item.category ?? "Guide"}</span>
        <span>{item.readingTime ?? "Read guide"}</span>
        <span className="text-moss transition-transform group-hover:translate-x-1">
          Read →
        </span>
      </div>
    </a>
  );
}
